document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll("[data-sort-select]").forEach((select) => {
    select.addEventListener("change", () => {
      select.form?.submit();
    });
  });

  const initScrollReveal = () => {
    const revealSelectors = [
      ".section-header",
      ".product-card",
      ".category-tile",
      ".story-band__copy",
      ".story-band__media",
      ".newsletter",
      ".page-hero",
      ".collection-toolbar",
      ".featured-collection__footer",
      ".product-page > *",
      ".cart-table",
      ".cart-summary",
      ".rte",
      ".search-form",
      ".empty-state",
      ".site-footer__grid > *",
    ];
    const elements = Array.from(document.querySelectorAll(revealSelectors.join(","))).filter(
      (element) => !element.closest(".yi-hero") && !element.closest(".site-header")
    );

    if (!elements.length) return;

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion || !("IntersectionObserver" in window)) {
      elements.forEach((element) => element.classList.add("is-visible"));
      return;
    }

    elements.forEach((element, index) => {
      element.classList.add("reveal-item");
      element.style.setProperty("--reveal-delay", `${Math.min((index % 4) * 70, 210)}ms`);
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        });
      },
      {
        rootMargin: "0px 0px -12% 0px",
        threshold: 0.12,
      }
    );

    elements.forEach((element) => observer.observe(element));
  };

  initScrollReveal();

  const siteHeader = document.querySelector(".site-header");
  if (siteHeader) {
    const updateHeaderState = () => {
      siteHeader.classList.toggle("is-scrolled", window.scrollY > 8);
    };
    window.addEventListener("scroll", updateHeaderState, { passive: true });
    updateHeaderState();
  }

  const heroMedia = document.querySelector(".yi-hero__media");
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (heroMedia && !reducedMotion) {
    let parallaxQueued = false;
    const updateParallax = () => {
      parallaxQueued = false;
      const y = window.scrollY;
      if (y <= window.innerHeight * 1.2) {
        heroMedia.style.transform = `translateY(${(y * 0.14).toFixed(1)}px)`;
      }
    };
    window.addEventListener(
      "scroll",
      () => {
        if (!parallaxQueued) {
          parallaxQueued = true;
          requestAnimationFrame(updateParallax);
        }
      },
      { passive: true }
    );
  }

  const mainImage = document.querySelector("[data-main-image]");
  const mediaThumbs = Array.from(document.querySelectorAll("[data-media-thumb]"));

  const setMainImage = (source, alt) => {
    if (!mainImage || !source) return;
    mainImage.removeAttribute("srcset");
    mainImage.removeAttribute("sizes");
    mainImage.src = source;
    if (alt) mainImage.alt = alt;
  };

  const activateThumb = (thumb) => {
    mediaThumbs.forEach((button) => button.classList.toggle("is-active", button === thumb));
    setMainImage(thumb.dataset.mediaFull, thumb.querySelector("img")?.alt);
  };

  mediaThumbs.forEach((thumb) => {
    thumb.addEventListener("click", () => activateThumb(thumb));
  });

  document.querySelectorAll(".product-form").forEach((form) => {
    const variantsNode = form.querySelector("[data-product-variants]");
    const variantInput = form.querySelector("[data-variant-id]");
    const submitButton = form.querySelector("[name='add']");
    const summary = form.querySelector("[data-product-selection-summary]");
    const price = document.querySelector("[data-product-price]");
    const comparePrice = document.querySelector("[data-product-compare-price]");
    const saleBadge = document.querySelector("[data-product-sale-badge]");
    const optionInputs = Array.from(form.querySelectorAll("[data-option-position]"));

    if (!variantsNode || !variantInput || !submitButton) return;

    let variants = [];
    try {
      variants = JSON.parse(variantsNode.textContent);
    } catch {
      return;
    }

    const moneyFormatter = new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: window.Shopify?.currency?.active || "USD",
    });

    const formatMoney = (cents) => moneyFormatter.format(cents / 100);

    let hasInteracted = false;

    const selectedOptions = () => {
      const options = [];
      form.querySelectorAll("[data-option-position]:checked").forEach((input) => {
        options[Number(input.dataset.optionPosition) - 1] = input.value;
      });
      return options;
    };

    const setSelectedLabels = (options) => {
      options.forEach((value, index) => {
        const label = form.querySelector(`[data-selected-option="${index + 1}"]`);
        if (label) label.textContent = value;
      });
    };

    const optionValuesForVariant = (variant) =>
      variant.options || [variant.option1, variant.option2, variant.option3].filter(Boolean);

    const updateSelectedClasses = () => {
      form.querySelectorAll(".product-option").forEach((label) => {
        const input = label.querySelector("input");
        label.classList.toggle("is-selected", Boolean(input?.checked));
      });
    };

    const updateVariant = () => {
      const options = selectedOptions();
      const variant = variants.find((candidate) =>
        optionValuesForVariant(candidate).every((value, index) => value === options[index])
      );

      setSelectedLabels(options);
      updateSelectedClasses();

      if (!variant) {
        submitButton.disabled = true;
        submitButton.textContent = submitButton.dataset.soldText || "Sold out";
        if (summary) summary.textContent = "Selected combination is unavailable.";
        return;
      }

      variantInput.value = variant.id;
      submitButton.disabled = !variant.available;
      submitButton.textContent = variant.available
        ? submitButton.dataset.addText || "Add to cart"
        : submitButton.dataset.soldText || "Sold out";

      if (summary) summary.textContent = `Selected: ${variant.title}`;
      if (price && typeof variant.price === "number") {
        price.textContent = formatMoney(variant.price);
      }

      const onSale = typeof variant.compare_at_price === "number" && variant.compare_at_price > variant.price;
      if (comparePrice) {
        comparePrice.hidden = !onSale;
        if (onSale) comparePrice.textContent = formatMoney(variant.compare_at_price);
      }
      if (saleBadge) saleBadge.hidden = !onSale;

      if (hasInteracted && variant.featured_media?.id) {
        const matchingThumb = mediaThumbs.find(
          (thumb) => thumb.dataset.mediaId === String(variant.featured_media.id)
        );
        if (matchingThumb) {
          activateThumb(matchingThumb);
        } else if (variant.featured_media.preview_image?.src) {
          const src = variant.featured_media.preview_image.src;
          setMainImage(src + (src.includes("?") ? "&" : "?") + "width=1500");
        }
      }
    };

    if (!optionInputs.length) return;

    optionInputs.forEach((input) => {
      input.addEventListener("change", () => {
        hasInteracted = true;
        updateVariant();
      });
    });

    updateVariant();
  });
});
